import { FC, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import useSessionArchive, {
  useArchiveFrameworks,
  type SessionArchiveRow,
} from '@/api/useSessionArchive.ts';
import Routes from '@/router/Routes.ts';
import { FRAMEWORK_BY_ID, type FrameworkId } from '@/constants/frameworks.ts';
import { cn } from '@/utils/classNames.ts';
import {
  buildSessionDetailNavigation,
  formatGovScore,
  resolveSessionAction,
  resolveSessionOpenRoute,
} from '@/utils/sessionTableDisplay.ts';

const columnHelper = createColumnHelper<SessionArchiveRow>();
const PAGE_SIZE = 10;

type Tab = 'all' | 'attention';

function frameworkLabel(framework: string | null): string {
  if (!framework) return '—';
  return FRAMEWORK_BY_ID[framework as FrameworkId]?.label ?? framework;
}

const SessionArchivePage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('all');
  const [frameworkFilter, setFrameworkFilter] = useState<string>('all');
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useSessionArchive({
    page,
    pageSize: PAGE_SIZE,
    frameworkFilter,
    attentionOnly: tab === 'attention',
  });

  const { data: frameworks = [] } = useArchiveFrameworks();

  const pageRows = data?.rows ?? [];
  const totalCount = data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);

  const columns = useMemo(
    () => [
      columnHelper.accessor('title', {
        header: () => t('sessionArchivePage.columnTitle'),
        cell: (info) => <span className="font-dm text-sm font-bold text-black/87">{info.getValue() ?? '—'}</span>,
      }),
      columnHelper.accessor('framework', {
        header: () => t('sessionArchivePage.columnFramework'),
        cell: (info) => <span className="font-dm text-sm text-black/70">{frameworkLabel(info.getValue())}</span>,
      }),
      columnHelper.accessor('compassScore', {
        header: () => t('sessionArchivePage.columnCompass'),
        cell: (info) => (
          <span className="font-dm text-sm font-bold text-black/87">{formatGovScore(info.getValue())}</span>
        ),
      }),
      columnHelper.accessor('lifecycleStatus', {
        header: () => t('sessionArchivePage.columnLifecycle'),
        cell: (info) => (
          <span className="font-dm text-sm text-black/70">{info.getValue().replaceAll('_', ' ')}</span>
        ),
      }),
      columnHelper.accessor('statusLabel', {
        header: () => t('sessionArchivePage.columnStatus'),
        cell: (info) => (
          <span
            className={cn(
              'font-dm text-sm font-bold',
              info.getValue() === 'Fiduciary Review Required'
                ? 'text-[#D93025]'
                : info.getValue() === 'Governance Caution'
                  ? 'text-[#B38600]'
                  : 'text-[#00874C]',
            )}
          >
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('lastGovernanceEvent', {
        header: () => t('sessionArchivePage.columnLastEvent'),
        cell: (info) => <span className="font-dm text-sm text-black/60">{info.getValue() ?? '—'}</span>,
      }),
      columnHelper.accessor('updatedAt', {
        header: () => t('sessionArchivePage.columnUpdatedAt'),
        cell: (info) => <span className="font-dm text-sm text-black/60">{dayjs(info.getValue()).format('MMM D')}</span>,
      }),
      columnHelper.display({
        id: 'action',
        header: () => t('sessionArchivePage.columnAction'),
        enableSorting: false,
        cell: ({ row }) => {
          const { id, lifecycleStatus, pendingReview, framework, compassScore } = row.original;
          const action = resolveSessionAction(lifecycleStatus, pendingReview);
          const frameworkAttribution =
            (framework && FRAMEWORK_BY_ID[framework as FrameworkId]?.label) || framework || undefined;

          if (action === 'open_session') {
            return (
              <button
                type="button"
                className="font-dm text-sm font-bold text-[#0F1B2A] underline underline-offset-2"
                onClick={() =>
                  navigate(resolveSessionOpenRoute(lifecycleStatus), { state: { decisionId: id } })
                }
              >
                {t('sessionArchivePage.continueGovernedDecision')}
              </button>
            );
          }

          if (action === 'view_record') {
            const { pathname, state } = buildSessionDetailNavigation(id);
            return (
              <button
                type="button"
                className="font-dm text-sm font-bold text-[#0F1B2A] underline underline-offset-2"
                onClick={() => navigate(pathname, { state })}
              >
                {t('sessionArchivePage.viewExecutionRecord')}
              </button>
            );
          }

          if (action === 'complete_session') {
            return (
              <button
                type="button"
                className="font-dm text-sm font-bold text-[#0F1B2A] underline underline-offset-2"
                onClick={() =>
                  navigate(Routes.RECOMMENDATION_ENGINE, {
                    state: {
                      decisionId: id,
                      resumeStep: 'compass-review',
                      frameworkAttribution,
                      governanceScore: compassScore,
                    },
                  })
                }
              >
                {t('sessionArchivePage.completeGovernedDecision')}
              </button>
            );
          }

          return <span className="font-dm text-sm text-black/35">—</span>;
        },
      }),
    ],
    [navigate, t],
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 bg-[#F4F9FF] rounded-xl">
        <div className="flex flex-col gap-1">
          <h1 className="font-newsreader text-[28px] font-extrabold leading-[125%] text-black/87">
            {t('sessionArchivePage.title')}
          </h1>
          <p className="font-dm text-sm text-black/60">{t('sessionArchivePage.subtitle')}</p>
        </div>
        <button
          type="button"
          className="font-dm px-[22px] py-2 rounded-xl bg-[#0F1B2A] text-[#FAFAFA] font-bold text-sm whitespace-nowrap"
          onClick={() => navigate(Routes.COMMON_INTAKE)}
        >
          {t('sessionArchivePage.startNewSession')}
        </button>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          {(['all', 'attention'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setTab(key);
                setPage(1);
              }}
              className={cn(
                'px-4 py-2 rounded-xl text-sm font-dm font-bold border transition-colors',
                tab === key ? 'bg-[#0F1B2A] text-white border-[#0F1B2A]' : 'bg-white text-black/60 border-base-300',
              )}
            >
              {key === 'all' ? t('sessionArchivePage.tabAll') : t('sessionArchivePage.tabAttention')}
            </button>
          ))}

          <select
            value={frameworkFilter}
            onChange={(event) => {
              setFrameworkFilter(event.target.value);
              setPage(1);
            }}
            className="select select-bordered h-10 min-h-10 text-sm font-dm"
          >
            <option value="all">{t('sessionArchivePage.filterAllFrameworks')}</option>
            {frameworks.map((framework) => (
              <option key={framework} value={framework}>
                {frameworkLabel(framework)}
              </option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <LoaderWrapper isLoading />
        ) : isError ? (
          <p className="font-dm text-sm text-center text-[#D93025] py-8">{t('sessionArchivePage.error')}</p>
        ) : totalCount === 0 ? (
          <div className="rounded-2xl bg-[#F4F9FF] px-8 py-10 text-center flex flex-col items-center gap-4">
            <p className="font-dm text-sm text-black/70">{t('sessionArchivePage.empty')}</p>
            <button
              type="button"
              className="font-dm px-5 py-2 rounded-xl bg-[#0F1B2A] text-[#FAFAFA] font-bold text-sm"
              onClick={() => navigate(Routes.COMMON_INTAKE)}
            >
              {t('sessionArchivePage.startNewSession')}
            </button>
          </div>
        ) : (
          <>
            <DataTable
              data={pageRows}
              columns={columns}
              zebra
              className="table-auto"
              wrapperClassName="w-full rounded-2xl border border-base-300 bg-base-100"
            />

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={currentPage <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
              >
                {t('sessionArchivePage.previous')}
              </button>
              <span className="font-dm text-sm text-black/60">
                {t('sessionArchivePage.pagination', { currentPage, pageCount })}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={currentPage >= pageCount}
                onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
              >
                {t('sessionArchivePage.next')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SessionArchivePage;
